import { useMemo, useState, useEffect } from 'react';
import { log, LOG_LEVELS } from '@common/config/app_logger';
import { isMobile } from 'react-device-detect';
import env from '@app/config/variables';
import { isPrerenderMode } from '@app/config/util';

const { WEB_IMAGE_SCALING_MODULO } = env;

// Global cache for image URLs with metadata (tracking which sizes we've used)
interface CachedImageInfo {
  url: string;
  width: number;
  height: number;
  loaded?: boolean; // Track if image is already loaded in browser
}
const imageUrlCache = new Map<string, CachedImageInfo>();
const MAX_WIDTH = 500;

// Global cache for preloaded images (browser-level cache)
const preloadedImages = new Map<string, HTMLImageElement>();

/**
 * Custom hook for generating optimized image URLs with caching
 */
interface UseOptimizedImageParams {
  imageUrl: string;
  imageDetails: { host: string; key: string };
  retinaFactor?: number;
  enableCaching?: boolean;
  desktopSize?: [number, number]; // [width, height]
  mobileSize?: [number, number]; // [width, height]
}

// Function to apply MAX_WIDTH limit to dimensions (maintaining aspect ratio)
const applyMaxWidthLimit = (width: number, height: number, retinaFactor: number): { width: number; height: number } => {
  let widthValue = width * retinaFactor;
  let heightValue = height * retinaFactor;

  if (widthValue > MAX_WIDTH) {
    widthValue = MAX_WIDTH;
    heightValue = MAX_WIDTH;
  }

  return { width: Math.round(widthValue), height: Math.round(heightValue) };
};

// Function to generate cache key with width and height (using limited dimensions)
const getCacheKey = (imageKey: string, width: number, height: number, host: string, retinaFactor: number): string => {
  const limited = applyMaxWidthLimit(width, height, retinaFactor);
  return `${ host }_${ imageKey }_${ limited.width }x${ limited.height }`;
};

// Function to find larger cached image (use larger cached image if available)
const findLargerCachedImage = (
  imageKey: string,
  targetWidth: number,
  targetHeight: number,
  host: string,
  retinaFactor: number
): CachedImageInfo | null => {
  // Apply MAX_WIDTH limit to target dimensions for consistent comparison
  const targetLimited = applyMaxWidthLimit(targetWidth, targetHeight, retinaFactor);
  const targetArea = targetLimited.width * targetLimited.height;
  let bestMatch: CachedImageInfo | null = null;

  // Search through cache for larger images of the same asset
  const cacheEntries = Array.from(imageUrlCache.entries());
  cacheEntries.forEach(([cacheKey, cachedInfo]) => {
    if (cacheKey.startsWith(`${ host }_${ imageKey }_`)) {
      const cachedArea = cachedInfo.width * cachedInfo.height;

      // Only consider images that are larger than or equal to target
      if (cachedArea >= targetArea) {
        if (!bestMatch || cachedArea < (bestMatch.width * bestMatch.height)) {
          // Prefer the smallest larger image to save bandwidth
          bestMatch = cachedInfo;
        }
      }
    }
  });

  return bestMatch;
};

// Function to find smaller cached image (use as placeholder while loading target image)
const findSmallerCachedImage = (
  imageKey: string,
  targetWidth: number,
  targetHeight: number,
  host: string,
  retinaFactor: number
): CachedImageInfo | null => {
  // Apply MAX_WIDTH limit to target dimensions for consistent comparison
  const targetLimited = applyMaxWidthLimit(targetWidth, targetHeight, retinaFactor);
  const targetArea = targetLimited.width * targetLimited.height;
  let bestMatch: CachedImageInfo | null = null;

  // Search through cache for smaller images of the same asset
  const cacheEntries = Array.from(imageUrlCache.entries());
  cacheEntries.forEach(([cacheKey, cachedInfo]) => {
    if (cacheKey.startsWith(`${ host }_${ imageKey }_`)) {
      const cachedArea = cachedInfo.width * cachedInfo.height;

      // Only consider images that are smaller than target (but not too small - at least 25% of target area)
      if (cachedArea < targetArea && cachedArea >= targetArea * 0.25) {
        if (!bestMatch || cachedArea > (bestMatch.width * bestMatch.height)) {
          // Prefer the largest smaller image for better quality placeholder
          bestMatch = cachedInfo;
        }
      }
    }
  });

  return bestMatch;
};

// Function to create optimized image URL
const createOptimizedImageUrl = (
  imageKey: string,
  width: number,
  height: number,
  host: string,
  retinaFactor: number
): string | null => {
  if (!imageKey || !width || !height || !host) return null;

  // Apply MAX_WIDTH limit to ensure consistency with cache key
  const limited = applyMaxWidthLimit(width, height, retinaFactor);

  const config = {
    key: imageKey,
    edits: {
      resize: {
        width: limited.width,
        height: limited.height,
        fit: 'contain'
      }
    }
  };

  try {
    const encodedConfig = btoa(JSON.stringify(config));
    return `${ host }/${ encodedConfig }`;
  } catch (error) {
    log(LOG_LEVELS.ERROR, 'Failed to create optimized image URL:', error);
    return null;
  }
};

// Function to cache image URL with metadata
const cacheImageUrl = (
  url: string,
  cacheKey: string,
  width: number,
  height: number,
  retinaFactor: number
): void => {
  // Use limited dimensions to match cache key
  const limited = applyMaxWidthLimit(width, height, retinaFactor);
  imageUrlCache.set(cacheKey, {
    url,
    width: limited.width,
    height: limited.height
  });
  log(LOG_LEVELS.INFO, 'Cached image URL for:', cacheKey);
};

export const useOptimizedImage = ({
  imageUrl,
  imageDetails,
  retinaFactor = WEB_IMAGE_SCALING_MODULO,
  enableCaching = true,
  desktopSize,
  mobileSize
}: UseOptimizedImageParams): string | null => {
  // State to track which image URL to display (placeholder or target)
  const [displayUrl, setDisplayUrl] = useState<string | null>(null);

  // Calculate target dimensions and URLs
  const imageData = useMemo(() => {
    if (!imageUrl || !imageDetails || (typeof imageDetails === 'object' && Object.keys(imageDetails).length === 0)) {
      return null;
    }

    // Get dimensions based on device type and provided sizes
    let width: number;
    let height: number;

    if (isMobile && desktopSize) {
      [width, height] = desktopSize;
    } else if (!isMobile && desktopSize) {
      [width, height] = desktopSize;
    } else {
      width = MAX_WIDTH;
      height = MAX_WIDTH;
    }

    const cacheKey = getCacheKey(imageDetails.key, width, height, imageDetails.host, retinaFactor);
    let targetImageUrl: string | null = null;
    let placeholderImageUrl: string | null = null;

    // Check if we have exact size in cache
    if (enableCaching) {
      const exactCachedInfo = imageUrlCache.get(cacheKey);
      if (exactCachedInfo) {
        log(LOG_LEVELS.INFO, 'Using exact cached image URL for:', cacheKey);
        // If exact match is found and already loaded, use it directly
        if (exactCachedInfo.loaded || preloadedImages.has(exactCachedInfo.url)) {
          return {
            targetUrl: exactCachedInfo.url,
            placeholderUrl: null,
            useTargetImmediately: true
          };
        }
        targetImageUrl = exactCachedInfo.url;
      } else {
        // Check for larger cached image (use it directly, no need to create new URL)
        const largerCachedInfo = findLargerCachedImage(imageDetails.key, width, height, imageDetails.host, retinaFactor);
        if (largerCachedInfo) {
          log(LOG_LEVELS.INFO, 'Using larger cached image URL:', cacheKey);
          if (largerCachedInfo.loaded || preloadedImages.has(largerCachedInfo.url)) {
            return {
              targetUrl: largerCachedInfo.url,
              placeholderUrl: null,
              useTargetImmediately: true
            };
          }
          targetImageUrl = largerCachedInfo.url;
        }
      }

      // Find smaller cached image for placeholder
      if (targetImageUrl) {
        const smallerCachedInfo = findSmallerCachedImage(imageDetails.key, width, height, imageDetails.host, retinaFactor);
        if (smallerCachedInfo) {
          log(LOG_LEVELS.INFO, 'Found smaller cached image for placeholder:', cacheKey);
          placeholderImageUrl = smallerCachedInfo.url;
        }
      }
    }

    // Create new optimized image URL if not found in cache
    if (!targetImageUrl) {
      targetImageUrl = createOptimizedImageUrl(
        imageDetails.key,
        width,
        height,
        imageDetails.host,
        retinaFactor
      );

      if (!targetImageUrl) {
        return null;
      }

      // Add to cache if caching is enabled
      if (enableCaching) {
        cacheImageUrl(targetImageUrl, cacheKey, width, height, retinaFactor);
      }

      // Find smaller cached image for placeholder
      if (enableCaching) {
        const smallerCachedInfo = findSmallerCachedImage(imageDetails.key, width, height, imageDetails.host, retinaFactor);
        if (smallerCachedInfo) {
          log(LOG_LEVELS.INFO, 'Found smaller cached image for placeholder:', cacheKey);
          placeholderImageUrl = smallerCachedInfo.url;
        }
      }
    }

    return {
      targetUrl: targetImageUrl,
      placeholderUrl: placeholderImageUrl,
      useTargetImmediately: false
    };
  }, [imageUrl, imageDetails?.key, imageDetails?.host, retinaFactor, enableCaching, desktopSize, mobileSize, isMobile]);

  // Update URLs when imageData changes
  useEffect(() => {
    if (!imageData || isPrerenderMode()) {
      setDisplayUrl(null);
      return;
    }

    if (imageData.useTargetImmediately) {
      // Use target immediately if it's already loaded
      setDisplayUrl(imageData.targetUrl);
      return;
    }

    // Set placeholder as initial display if available
    if (imageData.placeholderUrl) {
      setDisplayUrl(imageData.placeholderUrl);
    } else {
      setDisplayUrl(imageData.targetUrl);
    }

    // Preload target image
    if (imageData.targetUrl && imageData.targetUrl !== imageData.placeholderUrl) {
      // Check if already preloaded
      if (preloadedImages.has(imageData.targetUrl)) {
        setDisplayUrl(imageData.targetUrl);
        return;
      }

      const img = new Image();
      img.onload = () => {
        // Mark as loaded in cache
        preloadedImages.set(imageData.targetUrl!, img);

        // Update cache entry if exists
        const cacheEntries = Array.from(imageUrlCache.entries());
        const matchingEntry = cacheEntries.find(([, info]) => info.url === imageData.targetUrl);
        if (matchingEntry) {
          const [key, info] = matchingEntry;
          imageUrlCache.set(key, { ...info, loaded: true });
        }

        // Switch to target image once loaded
        setDisplayUrl(imageData.targetUrl);
        log(LOG_LEVELS.INFO, 'Target image loaded, switched from placeholder');
      };
      img.onerror = () => {
        log(LOG_LEVELS.ERROR, 'Failed to load target image:', imageData.targetUrl);
        // Keep placeholder if target fails to load
      };
      img.src = imageData.targetUrl;
    }
  }, [imageData]);

  return displayUrl;
};

// Export utility functions
export const clearGlobalImageCache = (): void => {
  imageUrlCache.clear();
  log(LOG_LEVELS.INFO, 'Global image URL cache cleared');
};

export const getImageCacheSize = (): number => imageUrlCache.size;
